<template>
  <div class="relative mb-8 card p-0">
    <div class="border-b border-main px-6">
      <ContentCardSubheader class="flex">
        <div>{{ t('Reviews.product.view.campaignLinksTable.title') }}</div>
        <TooltipWrapper top>
          <template #trigger>
            <a href="https://hi.omr.com/de/success-hub/omr-reviews-bewertungslinks" target="_blank">
              <CircleHelpIcon class="ml-1 lucide-icon-s align-middle text-icon-subtlest" />
            </a>
          </template>
          <template #content>
            <div v-html="t('Reviews.product.view.campaignLinksTable.campaignLinksTooltip')"></div>
          </template>
        </TooltipWrapper>

        <template #actions>
          <RouterLink
            :to="{
              name: ReviewsRouteName.ReviewsProductCampaignPersonalizationNew,
              params: { campaignId: campaign.id },
            }"
            class="btn-secondary-color-xs p-2 whitespace-nowrap"
            data-testid="reviews-campaign-create"
            @click="
              track('campaign_link_add', { surface: 'editor', entity_type: 'tool', entity_id: productId, campaign_id: campaign.id ?? '' })
            "
          >
            {{ t('Reviews.product.campaign.action.createCampaignLink') }}<CirclePlusIcon class="lucide-icon-s" />
          </RouterLink>
        </template>
      </ContentCardSubheader>
    </div>

    <div v-if="hasPersonalisedLinks" class="px-6">
      <TableLight>
        <thead>
          <tr>
            <th class="w-24 text-left">{{ t('Reviews.product.view.campaignLinksTable.languageLabel') }}</th>
            <th class="text-left">{{ t('Reviews.product.view.campaignLinksTable.personalLinksLabel') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="personalization in campaign.personalizations" :key="personalization.inviteMessage">
            <td>
              <div class="text-left">
                <ChipBadge size="m" rounded>
                  {{ personalization.locale.toUpperCase() }}
                </ChipBadge>
              </div>
            </td>
            <td>
              <div class="flex justify-between text-left">
                <div class="pr-8">{{ personalization.inviteMessage }}</div>
                <div class="flex">
                  <TooltipWrapper top>
                    <template #trigger>
                      <IconButton
                        :icon="CopyIcon"
                        size="extra-small"
                        variant="ghost-neutral"
                        :data-testid="`copy-personalization-link-${personalization.id}`"
                        @click="handleCopyPersonalizationLink(personalization)"
                      />
                    </template>
                    <template #content>
                      <div v-html="t('Reviews.product.view.campaignLinksTable.copyPersonalizedTooltip')"></div>
                    </template>
                  </TooltipWrapper>
                  <TooltipWrapper top>
                    <template #trigger>
                      <IconButton
                        :icon="PencilIcon"
                        size="extra-small"
                        variant="ghost-neutral"
                        @click="handleEditPersonalization(campaign.id, personalization.id)"
                      />
                    </template>
                    <template #content>
                      <div v-html="t('Reviews.product.view.campaignLinksTable.editPersonalizationTooltip')"></div>
                    </template>
                  </TooltipWrapper>
                  <TooltipWrapper top>
                    <template #trigger>
                      <IconButton
                        :icon="Trash2Icon"
                        size="extra-small"
                        variant="ghost-neutral"
                        @click="handleDeletePersonalization(personalization)"
                      />
                    </template>
                    <template #content>
                      <div v-html="t('Reviews.product.view.campaignLinksTable.deletePersonalizationTooltip')"></div>
                    </template>
                  </TooltipWrapper>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </TableLight>
    </div>
    <div class="mb-10 px-6" :class="{ 'mt-6': !hasPersonalisedLinks }">
      <GenericInvitationLinks
        subject="product"
        :campaign-hash="campaign.hashValue || undefined"
        :slug="productSlug"
        cta-position="product_campaign_links"
        orientation="row"
      />
    </div>
  </div>
  <ConfirmModal
    v-if="showDeleteConfirmationModal && personalizationLinkToDelete"
    wrapper-class="max-w-4xl"
    :title="t('Reviews.product.view.campaignLinksTable.deleteConfirmModal.title')"
    @close-dialog="onDeletePersonalization(false)"
  >
    <template #content>
      <MarkdownContent class="px-6 pb-3" unstyled :md="personalizationLinkToDelete" />
    </template>
    <template #actions>
      <div class="mt-6 flex justify-end px-6 pb-6">
        <button class="btn-secondary-color-s ml-4" data-testid="accept-button" @click="onDeletePersonalization(false)">
          {{ t('Reviews.product.action.cancel') }}
        </button>
        <button class="btn-primary-destructive-s ml-4" data-testid="reviews-campaign-del-confirm" @click="onDeletePersonalization(true)">
          {{ t('Reviews.product.view.campaignLinksTable.deleteConfirmModal.confirm') }}
        </button>
      </div>
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
import { ChipBadge, TooltipWrapper, IconButton, MarkdownContent } from '@ramp106/omrjs-core-ui'
import { CircleHelpIcon, CopyIcon, PencilIcon, CirclePlusIcon, Trash2Icon } from '@ramp106/omrjs-core-ui/icons'
import { reviewsPaths } from '@ramp106/omrjs-routing'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { ConfirmModal } from '@/components'
import { baseOmrUrl } from '@/config/env'
import type { IncentivizationCampaign } from '@/gql/reviews'
import { useDeleteCampaignPersonalizationMutation } from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import GenericInvitationLinks from './GenericInvitationLinks.vue'

type Personalization = NonNullable<IncentivizationCampaign['personalizations']>[number]

const props = defineProps<{
  campaign: IncentivizationCampaign
  productId: string
  productSlug: string
}>()

const { t } = useI18n()
const router = useRouter()

const showDeleteConfirmationModal = ref(false)
const personalizationToDelete = ref<Personalization | null>(null)
const personalizationLinkToDelete = ref<string | null>(null)
const hasPersonalisedLinks = computed(() => props.campaign.personalizations && props.campaign.personalizations.length > 0)

const { mutate: mutateDeleteCampaignPersonalization } = useDeleteCampaignPersonalizationMutation({
  clientId: 'reviews',
  refetchQueries: ['reviewsIncentivizationCampaign'],
})

function getPersonalizationLink(personalization: Personalization) {
  return `${baseOmrUrl}${reviewsPaths.productTrustBuilding({ locale: personalization.locale, slug: props.productSlug }, { campaign: personalization.hashValue })}`
}

function handleEditPersonalization(campaignId: string | null | undefined, personalizationId: string | null | undefined) {
  if (!campaignId || !personalizationId) {
    return
  }
  router.push({
    name: ReviewsRouteName.ReviewsProductCampaignPersonalizationEdit,
    params: { campaignId, personalizationId },
  })
}

function handleCopyPersonalizationLink(personalization: Personalization) {
  track('cta_click', {
    surface: 'cta',
    category: 'engagement',
    cta_id: 'personalized_survey_link_copy',
    cta_position: 'product_campaign_links',
  })
  const link = getPersonalizationLink(personalization)
  navigator.clipboard.writeText(link)
  showNotification(t('Reviews.product.view.campaignLinksTable.linkCopiedMessage'), 'success')
}

function handleDeletePersonalization(personalization: Personalization) {
  personalizationToDelete.value = personalization
  const personalizationLink = getPersonalizationLink(personalization)
  personalizationLinkToDelete.value = `[${personalizationLink}](${personalizationLink})`
  showDeleteConfirmationModal.value = true
}

async function onDeletePersonalization(confirmed: boolean) {
  if (confirmed && personalizationToDelete.value?.id) {
    try {
      await mutateDeleteCampaignPersonalization({ campaignPersonalization: personalizationToDelete.value.id })
      showNotification(t('Reviews.product.view.campaignLinksTable.deleteSuccess'), 'success')
    } catch (_e) {
      showNotification(t('Reviews.product.view.campaignLinksTable.deleteError'), 'error')
    }
  }

  showDeleteConfirmationModal.value = false
  personalizationToDelete.value = null
  personalizationLinkToDelete.value = null
}
</script>
